/**
 * GDL Tracking SDK
 *
 * Builds GA4-shaped payloads and publishes them to the Shopify Custom Pixel
 * sandbox via Shopify.analytics.publish().
 *
 * Data sources:
 * - window.__TRACKING__  (rendered by snippets/gdl-tracking-data.liquid)
 * - Attribution store    (sessionStorage, carries item_list_name/index/category/
 *                         color/stock across page loads and into the cart)
 *
 * Conventions agreed for this theme:
 * - item_name        = full product title
 * - item_color       = value of the "colore"/"color" variant option
 * - item_category    = first collection of the product whose handle is not "all"
 * - discount         = compare_at_price - price (0 when not discounted)
 * - item_isdiscounted= 'yes' | 'no'
 * - item_brand is hardcoded in the sandbox, so it is not sent here.
 *
 * To add a new event: add a method to `track` below, then wire its trigger in
 * gdl-tracking.js. Nothing else needs to change.
 */
window.TrackingSDK = (function () {
  function cents(n) {
    return (n || 0) / 100;
  }

  function getCurrency() {
    return (window.__TRACKING__ && window.__TRACKING__.page && window.__TRACKING__.page.currency) || 'EUR';
  }

  function availability(available) {
    return available ? 'in stock' : 'out of stock';
  }

  // Static per-variant data rendered in Liquid for the products currently in the cart.
  function cartLineContext(variantId) {
    var map = (window.__TRACKING__ && window.__TRACKING__.cart_items) || {};
    return map[String(variantId)] || null;
  }

  // =============================
  // ATTRIBUTION STORE
  // =============================
  var Attribution = (function () {
    var KEY = '_gdl_tracking_attr';

    function load() {
      try {
        return JSON.parse(sessionStorage.getItem(KEY)) || {};
      } catch (e) {
        return {};
      }
    }

    function save(data) {
      try {
        sessionStorage.setItem(KEY, JSON.stringify(data));
      } catch (e) {}
    }

    return {
      set: function (id, ctx) {
        var d = load();
        d[String(id)] = ctx;
        save(d);
      },
      get: function (id) {
        var d = load();
        return Object.prototype.hasOwnProperty.call(d, String(id)) ? d[String(id)] : null;
      },
    };
  })();

  // =============================
  // BUILD ITEM  (view_item)
  //
  // data: {
  //   id, variant_id, title, category, color, sku,
  //   price (cents), compare_at_price (cents),
  //   available, inventory_quantity, variant_title
  // }
  // =============================
  function buildItem(data, qty, ctx) {
    var attrCtx = ctx || Attribution.get(data.variant_id || data.id) || {};
    var price = cents(data.price);
    var cmp = cents(data.compare_at_price);
    var discounted = cmp > 0 && cmp > price;

    var item = {
      item_id: String(data.id),
      item_name: data.title || '',
      item_variant_id: data.variant_id != null ? String(data.variant_id) : null,
      item_variant: data.variant_title || '',
      item_sku: data.sku || '',
      item_category: data.category || attrCtx.category || '',
      item_color: data.color || attrCtx.color || '',
      item_availability: availability(data.available),
      stock_quantity: data.inventory_quantity != null ? data.inventory_quantity : attrCtx.stock_quantity || 0,
      currency: getCurrency(),
      price: price,
      discount: discounted ? Math.round((cmp - price) * 100) / 100 : 0,
      item_isdiscounted: discounted ? 'yes' : 'no',
      quantity: qty || 1,
    };

    if (attrCtx.item_list_name !== undefined) item.item_list_name = attrCtx.item_list_name;
    if (attrCtx.index !== undefined) item.index = attrCtx.index;

    return item;
  }

  // =============================
  // BUILD CART ITEM  (add_to_cart, from a /cart.js line item)
  // =============================
  function buildCartItem(cartItem, qty) {
    var ctx = Attribution.get(String(cartItem.variant_id)) || Attribution.get(String(cartItem.product_id)) || {};
    var liquidCtx = cartLineContext(cartItem.variant_id) || {};
    var price = cents(cartItem.price);
    var cmp = cents(cartItem.original_price > cartItem.price ? cartItem.original_price : 0);
    var discounted = cmp > 0 && cmp > price;

    var stock = Attribution.get('vstock:' + cartItem.variant_id);
    if (stock === null) {
      stock = liquidCtx.stock_quantity != null ? liquidCtx.stock_quantity : ctx.stock_quantity || 0;
    }

    var item = {
      item_id: String(cartItem.product_id),
      item_name: cartItem.product_title || cartItem.title || '',
      item_variant_id: String(cartItem.variant_id),
      item_variant: cartItem.variant_title || '',
      item_sku: cartItem.sku || '',
      item_category: ctx.category || liquidCtx.category || '',
      item_color: ctx.color || liquidCtx.color || '',
      item_availability: availability(stock > 0),
      stock_quantity: stock,
      currency: getCurrency(),
      price: price,
      discount: discounted ? Math.round((cmp - price) * 100) / 100 : 0,
      item_isdiscounted: discounted ? 'yes' : 'no',
      quantity: qty || cartItem.quantity || 1,
    };

    if (ctx.item_list_name !== undefined) item.item_list_name = ctx.item_list_name;
    if (ctx.index !== undefined) item.index = ctx.index;

    return item;
  }

  // =============================
  // PUBLISH
  // =============================
  function publish(name, payload) {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, payload);
    }
  }

  // =============================
  // TRACK API
  // =============================
  var track = {
    /**
     * view_item — fired on PDP load and on every variant change.
     * Also persists the product context so a later add_to_cart can be enriched.
     */
    viewItem: function (data) {
      var existing = Attribution.get(String(data.variant_id || data.id)) || {};
      var ctx = {
        category: data.category || '',
        color: data.color || '',
        stock_quantity: data.inventory_quantity || 0,
      };
      if (existing.item_list_name !== undefined) ctx.item_list_name = existing.item_list_name;
      if (existing.index !== undefined) ctx.index = existing.index;

      Attribution.set(String(data.variant_id || data.id), ctx);
      if (data.variant_id) Attribution.set(String(data.id), ctx);
      if (data.variants) {
        data.variants.forEach(function (v) {
          Attribution.set('vstock:' + v.id, v.inventory_quantity || 0);
        });
      }

      var item = buildItem(data, 1, ctx);
      publish('view_item', {
        event: 'view_item',
        ecommerce: {
          currency: getCurrency(),
          value: item.price,
          items: [item],
        },
      });
    },

    /**
     * add_to_cart — fired from the cart diff, so it covers every entry point
     * (PDP form, quick add, configurator, quantity increase in cart/drawer).
     * Note: the sandbox reads `page_currency` for this event, not `currency`.
     */
    addToCart: function (cartItem, qty) {
      var item = buildCartItem(cartItem, qty);
      publish('add_to_cart', {
        event: 'add_to_cart',
        ecommerce: {
          page_currency: getCurrency(),
          value: Math.round(item.price * item.quantity * 100) / 100,
          items: [item],
        },
      });
    },
  };

  return { track: track, buildItem: buildItem, buildCartItem: buildCartItem, Attribution: Attribution };
})();
